import { GetDropDownFilterProps } from "@/components/TableFilters/components/GetDropDownFilterProps";
import { GetSearchFilterProps } from "@/components/TableFilters/components/GetSearchFilterProps";
import {
  EntityStatus,
  FieldNames,
  FormType,
  PROJECT_STATUS,
  RIGHTS,
} from "@/enums";
import {
  buildProjectStatusOptions,
  generateDropDownOptions,
  getProjectStatusTranslationId,
  routes,
} from "@/helpers";
import {
  useFormPropertyValueLookupProvider,
  useUserContext,
} from "@/providers";
import { useGetProductCategoryFormsQuery } from "@/services";
import { Button, Table, Tooltip, Typography } from "antd";
import {
  any,
  array,
  arrayOf,
  bool,
  func,
  number,
  objectOf,
  oneOfType,
  string,
} from "prop-types";

import { faLock } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useIntl } from "react-intl";
import { Link } from "react-router-dom";
import ProjectNumberSearchFilter from "./ProjectNumberSearchFilter";
import RoleSearchFilter from "./RoleSearchFilter";
import {
  milestoneStatusOptions,
  milestoneSubmittedStatus,
  roleTypeOptions,
  userReplacementKeys,
  userReplacementStatus,
} from "./helpers";

const UserReplacementTable = ({
  data,
  isLoading,
  queryParams,
  setQueryParams,
  selectedRowKeys,
  setSelectedRowKeys,
  setSelectedRowsData,
}) => {
  const { getPropertyValueDisplay } = useFormPropertyValueLookupProvider();
  const intl = useIntl();
  const yesLabel = intl.formatMessage({ id: "general.yes" });
  const noLabel = intl.formatMessage({ id: "general.no" });
  const { data: forms } = useGetProductCategoryFormsQuery();
  const { hasRight } = useUserContext();

  const updateQueryParam = (key, value) => {
    const updatedQuery = { ...queryParams, page: 1 };
    updatedQuery[key] = value;
    setQueryParams(updatedQuery);
  };

  const yesNoOptions = [
    {
      id: "true",
      fallback: yesLabel,
    },
    {
      id: "false",
      fallback: noLabel,
    },
  ];

  const filterStatus = buildProjectStatusOptions().filter(
    (x) =>
      x.id !== PROJECT_STATUS.intake.canceled.id &&
      x.id !== PROJECT_STATUS.project.canceled.id &&
      x.id !== PROJECT_STATUS.project.complete.id
  );
  const roleSearchFilter = (confirm, inputRef) => (
    <RoleSearchFilter
      inputRef={inputRef}
      onReset={() => updateQueryParam(userReplacementKeys.role.key, undefined)}
      onSearch={(value) =>
        updateQueryParam(userReplacementKeys.role.key, value)
      }
      confirm={confirm}
      defaultValue={queryParams.role}
    />
  );

  const projectNumberSearchFilter = (confirm, inputRef) => (
    <ProjectNumberSearchFilter
      inputRef={inputRef}
      onReset={() =>
        updateQueryParam(userReplacementKeys.projectNumber.key, undefined)
      }
      onSearch={(value) =>
        updateQueryParam(userReplacementKeys.projectNumber.key, value)
      }
      confirm={confirm}
      defaultValue={queryParams.projectNumbers}
      userId={queryParams.userToBeReplaced}
    />
  );

  //Only show submitted status on filter dropdown
  const milestoneStatusOptionsBasedOnRight =
    !hasRight([RIGHTS.massReplaceUsers]) &&
    hasRight([RIGHTS.replaceProjectUsers])
      ? milestoneSubmittedStatus
      : milestoneStatusOptions;

  const columns = [
    {
      title: intl.formatMessage({ id: "userReplacement.colProjectNumber" }),
      ...GetSearchFilterProps({
        setQueryParamFunction: updateQueryParam,
        queryParamKey: userReplacementKeys.projectNumber.key,
        queryParamValue: queryParams[userReplacementKeys.projectNumber.key],
        customFilter: projectNumberSearchFilter,
      }),
      render: ({ id, projectNumber, projectId }) => (
        <Button data-test-id={`project-table-link-${id}`} type="link">
          <Link
            to={routes.projects.get(projectId)}
            target="_blank"
            rel="noopener noreferrer"
          >
            {projectNumber || "n/a"}
          </Link>
        </Button>
      ),
    },
    {
      title: intl.formatMessage({ id: "userReplacement.colProjectName" }),
      ...GetSearchFilterProps({
        setQueryParamFunction: updateQueryParam,
        queryParamKey: userReplacementKeys.projectName.key,
        queryParamValue: queryParams[userReplacementKeys.projectName.key],
      }),
      render: ({ projectName, secure }) => (
        <span>
          {secure && (
            <Tooltip
              title={intl.formatMessage({ id: "project.isSecureMessage" })}
              trigger={["hover"]}
            >
              <FontAwesomeIcon icon={faLock} className="mr-2" />
            </Tooltip>
          )}
          {projectName}
        </span>
      ),
    },
    {
      title: intl.formatMessage({ id: "userReplacement.colProjectType" }),
      dataIndex: userReplacementKeys.projectType.field,
      render: (value) =>
        getPropertyValueDisplay(FormType.Intake, FieldNames.ProjectType, value),
      ...GetDropDownFilterProps({
        setQueryParamFunction: updateQueryParam,
        queryParamKey: userReplacementKeys.projectType.key,
        queryParamValue: queryParams[userReplacementKeys.projectType.key],
        options: generateDropDownOptions(
          FieldNames.ProjectType,
          forms?.intake,
          EntityStatus
        ),
      }),
    },
    {
      title: intl.formatMessage({ id: "userReplacement.colProductCategory" }),
      dataIndex: userReplacementKeys.productCategory.field,
      render: (value) =>
        getPropertyValueDisplay(
          FormType.Intake,
          FieldNames.ProductCategory,
          value
        ),
      ...GetDropDownFilterProps({
        setQueryParamFunction: updateQueryParam,
        queryParamKey: userReplacementKeys.productCategory.key,
        queryParamValue: queryParams[userReplacementKeys.productCategory.key],
        options: generateDropDownOptions(
          FieldNames.ProductCategory,
          forms?.intake,
          EntityStatus
        ),
      }),
    },
    {
      title: intl.formatMessage({ id: "userReplacement.colProjectStatus" }),
      dataIndex: userReplacementKeys.projectStatus.field,
      render: (value) =>
        intl.formatMessage({ id: getProjectStatusTranslationId(value) }),
      ...GetDropDownFilterProps({
        setQueryParamFunction: updateQueryParam,
        queryParamKey: userReplacementKeys.projectStatus.key,
        queryParamValue: queryParams[userReplacementKeys.projectStatus.key],
        options: filterStatus.map((option) => ({
          ...option,
          fallback: intl.formatMessage({ id: option.translationId }),
        })),
      }),
    },
    {
      title: intl.formatMessage({ id: "userReplacement.colSecure" }),
      dataIndex: userReplacementKeys.secure.field,
      render: (value) => (value ? yesLabel : noLabel),
      ...GetDropDownFilterProps({
        singleChoice: true,
        setQueryParamFunction: updateQueryParam,
        queryParamKey: userReplacementKeys.secure.key,
        queryParamValue: queryParams[userReplacementKeys.secure.key],
        options: yesNoOptions,
      }),
    },
    {
      title: intl.formatMessage({ id: "userReplacement.colScheduleName" }),
      dataIndex: userReplacementKeys.scheduleName.field,
      ...GetSearchFilterProps({
        setQueryParamFunction: updateQueryParam,
        queryParamKey: userReplacementKeys.scheduleName.key,
        queryParamValue: queryParams[userReplacementKeys.scheduleName.key],
      }),
    },
    {
      title: intl.formatMessage({ id: "userReplacement.colMilestone" }),
      dataIndex: userReplacementKeys.milestone.field,
      ...GetSearchFilterProps({
        setQueryParamFunction: updateQueryParam,
        queryParamKey: userReplacementKeys.milestone.key,
        queryParamValue: queryParams[userReplacementKeys.milestone.key],
      }),
    },
    {
      title: intl.formatMessage({ id: "userReplacement.colMilestoneStatus" }),
      dataIndex: userReplacementKeys.milestoneStatus.field,
      ...GetDropDownFilterProps({
        setQueryParamFunction: updateQueryParam,
        queryParamKey: userReplacementKeys.milestoneStatus.key,
        queryParamValue: queryParams[userReplacementKeys.milestoneStatus.key],
        options: milestoneStatusOptionsBasedOnRight,
      }),
    },
    {
      title: intl.formatMessage({ id: "userReplacement.colRole" }),
      dataIndex: userReplacementKeys.role.field,
      ...GetSearchFilterProps({
        setQueryParamFunction: updateQueryParam,
        queryParamKey: userReplacementKeys.role.key,
        queryParamValue: queryParams[userReplacementKeys.role.key],
        customFilter: roleSearchFilter,
      }),
    },
    {
      title: intl.formatMessage({ id: "userReplacement.colRoleType" }),
      dataIndex: userReplacementKeys.roleType.field,
      ...GetDropDownFilterProps({
        setQueryParamFunction: updateQueryParam,
        queryParamKey: userReplacementKeys.roleType.key,
        queryParamValue: queryParams[userReplacementKeys.roleType.key],
        options: roleTypeOptions,
      }),
    },
    {
      title: intl.formatMessage({ id: "userReplacement.colNAEligible" }),
      dataIndex: userReplacementKeys.notApplicableEligible.field,
      render: (value, record) =>
        record.isProjectHeader ? "" : value ? yesLabel : noLabel,
      ...GetDropDownFilterProps({
        singleChoice: true,
        setQueryParamFunction: updateQueryParam,
        queryParamKey: userReplacementKeys.notApplicableEligible.key,
        queryParamValue:
          queryParams[userReplacementKeys.notApplicableEligible.key],
        options: yesNoOptions,
      }),
    },
    {
      title: intl.formatMessage({ id: "userReplacement.colAllowBlank" }),
      dataIndex: userReplacementKeys.allowBlank.field,
      render: (value, record) =>
        record.isProjectHeader ? "" : value ? yesLabel : noLabel,
      ...GetDropDownFilterProps({
        singleChoice: true,
        setQueryParamFunction: updateQueryParam,
        queryParamKey: userReplacementKeys.allowBlank.key,
        queryParamValue: queryParams[userReplacementKeys.allowBlank.key],
        options: yesNoOptions,
      }),
    },
  ];

  const rowSelection = {
    onSelect: ({ isProjectHeader, projectId }, selected, selectedRows) => {
      if (isProjectHeader) {
        const projectRows = data.filter(
          (x) =>
            x.projectId === projectId &&
            x.status !== userReplacementStatus.pending
        );
        const projectRowKeys = projectRows.map((x) => x.id);

        let selectedKeysUpdated = [];
        let selectedRowsUpdated = [];

        if (!selected) {
          selectedRowsUpdated = selectedRows.filter(
            (x) => x.projectId !== projectId
          );
          selectedKeysUpdated = selectedRowsUpdated.map((x) => x.id);
        } else {
          selectedKeysUpdated = [
            ...new Set([...selectedRowKeys, ...projectRowKeys]),
          ];
          selectedRowsUpdated = [...new Set([...selectedRows, ...projectRows])];
        }

        setSelectedRowKeys(selectedKeysUpdated);
        setSelectedRowsData(selectedRowsUpdated);
      } else {
        setSelectedRowKeys(selectedRows.map((x) => x.id));
        setSelectedRowsData(selectedRows);
      }
    },
    onSelectAll: (selected, selectedRows, changeRows) => {
      if (selected) {
        setSelectedRowsData(
          selectedRows.filter((x) => x.status !== userReplacementStatus.pending)
        );
        setSelectedRowKeys(
          data
            .filter((x) => x.status !== userReplacementStatus.pending)
            .map((x) => x.id)
        );
      } else {
        setSelectedRowKeys([]);
        setSelectedRowsData([]);
      }
    },
    selectedRowKeys: selectedRowKeys,
    getCheckboxProps: (record) => {
      let isPending = record.status === userReplacementStatus.pending;

      if (record.isProjectHeader) {
        isPending = !(
          data.filter(
            (x) =>
              !x.isProjectHeader &&
              x.projectId === record.projectId &&
              x.status !== userReplacementStatus.pending
          )?.length > 0
        );
      }

      if (!isPending) return;

      return {
        disabled: true,
        style: { display: "none" },
      };
    },
  };

  const getCurrentSelectionCount = () => {
    if (selectedRowKeys?.length) {
      const headerKeys = data.filter((x) => x.isProjectHeader).map((x) => x.id);
      return selectedRowKeys.filter((x) => !headerKeys.includes(x)).length;
    }
    return 0;
  };

  const getCurrentTotalCount = () => {
    if (data?.length) {
      return data.filter((x) => !x?.isProjectHeader).length;
    }
    return 0;
  };

  return (
    <>
      <Typography.Title level={5}>
        {intl.formatMessage(
          { id: "general.resultLabel" },
          {
            current: getCurrentSelectionCount(),
            total: getCurrentTotalCount(),
          }
        )}
      </Typography.Title>
      <Table
        rowSelection={{
          type: "checkbox",
          ...rowSelection,
        }}
        data-test-id="user-replacement-table"
        columns={columns}
        dataSource={data || []}
        rowKey={(r) => r.id}
        pagination={{
          showSizeChanger: true,
          position: ["bottomRight"],
        }}
        loading={isLoading}
        locale={{
          emptyText: intl.formatMessage({ id: "userReplacement.noData" }),
        }}
        scroll={{ x: 1500 }}
        rowClassName={(record) =>
          record.status === userReplacementStatus.pending
            ? "bg-disabled"
            : record.isProjectHeader
            ? "viper-table-header-color"
            : ""
        }
      />
    </>
  );
};

UserReplacementTable.propTypes = {
  isLoading: bool,
  data: any,
  queryParams: objectOf(oneOfType([string, number, arrayOf(string), bool]))
    .isRequired,
  setQueryParams: func.isRequired,
  selectedRowKeys: array,
  setSelectedRowKeys: func,
  setSelectedRowsData: func,
};

export default UserReplacementTable;
